(() => {
  const ROOT_SEL = '[data-search-modal]';
  const KEYWORD_RE = /cap\s*blast|opener|bottle/i;
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class SearchModalController {
    constructor(root) {
      this.root = root;
      this.panel = root.querySelector('.search-modal');
      this.overlay = root.querySelector('[data-search-modal-overlay]');
      this.closeBtn = root.querySelector('[data-search-modal-close]');
      this.form = root.querySelector('[data-search-form]');
      this.input = root.querySelector('[data-search-input]');
      this.hint = root.querySelector('[data-search-hint]');
      this.empty = root.querySelector('[data-search-empty]');
      this.card = root.querySelector('[data-search-card]');
      this.isOpen = false;
      this.lastFocus = null;
      this.inertTargets = [];

      this.onKeyDown = this.onKeyDown.bind(this);
      this.bindUI();
      this.bindGlobal();
      this.render('');
    }

    getPageShells() {
      return Array.from(
        document.querySelectorAll(
          '#MainContent, .shopify-section-group-header-group, .shopify-section-group-footer-group, [data-scroll-top]'
        )
      );
    }

    setPageInert(active) {
      if (active) {
        this.inertTargets = this.getPageShells();
        this.inertTargets.forEach((el) => {
          el.setAttribute('aria-hidden', 'true');
          el.setAttribute('inert', '');
        });
        return;
      }
      this.inertTargets.forEach((el) => {
        el.removeAttribute('aria-hidden');
        el.removeAttribute('inert');
      });
      this.inertTargets = [];
    }

    focusableNodes() {
      const scope = this.panel || this.root;
      return Array.from(scope.querySelectorAll(FOCUSABLE)).filter((el) => {
        if (el.closest('[inert]')) return false;
        if (el.getAttribute('aria-hidden') === 'true') return false;
        if (el.classList.contains('is-hidden')) return false;
        if (el.hidden) return false;
        const style = window.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden') return false;
        return true;
      });
    }

    trapFocus(event) {
      if (event.key !== 'Tab') return;
      const nodes = this.focusableNodes();
      if (!nodes.length) {
        event.preventDefault();
        (this.panel || this.root).focus?.();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;
      if (event.shiftKey) {
        if (active === first || !scopeContains(this.panel || this.root, active)) {
          event.preventDefault();
          last.focus();
        }
        return;
      }
      if (active === last || !scopeContains(this.panel || this.root, active)) {
        event.preventDefault();
        first.focus();
      }
    }

    bindUI() {
      this.overlay?.addEventListener('click', () => this.close());
      this.closeBtn?.addEventListener('click', () => this.close());

      this.input?.addEventListener('input', () => {
        this.render(this.input.value);
      });

      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.render(this.input?.value || '');
        if (this.card && !this.card.classList.contains('is-hidden')) {
          this.card.focus?.();
        }
      });

      this.card?.addEventListener('click', () => {
        this.close();
      });
    }

    bindGlobal() {
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-search-open]');
        if (!trigger) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();
        this.open();
      });
    }

    matches(query) {
      const q = String(query || '').trim();
      if (!q) return null;
      if (KEYWORD_RE.test(q)) return true;

      const keywords = (this.card?.getAttribute('data-search-keywords') || '')
        .split(',')
        .map((part) => part.trim().toLowerCase())
        .filter(Boolean);
      const needle = q.toLowerCase();
      if (keywords.some((word) => word.includes(needle) || needle.includes(word))) return true;
      return false;
    }

    render(query) {
      const q = String(query || '').trim();
      const match = this.matches(q);

      this.hint?.classList.toggle('is-hidden', q.length > 0);
      this.empty?.classList.toggle('is-hidden', !(q.length > 0 && match === false));
      this.card?.classList.toggle('is-hidden', match !== true);
    }

    open() {
      if (this.isOpen) return;
      this.lastFocus = document.activeElement;
      this.isOpen = true;
      this.root.classList.add('is-open');
      this.root.setAttribute('aria-hidden', 'false');
      this.root.removeAttribute('inert');
      this.setPageInert(true);
      document.body.classList.add('is-search-modal-open');
      document.addEventListener('keydown', this.onKeyDown);
      if (this.panel && !this.panel.hasAttribute('tabindex')) {
        this.panel.setAttribute('tabindex', '-1');
      }
      requestAnimationFrame(() => {
        this.input?.focus();
        this.input?.select?.();
      });
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.root.classList.remove('is-open');
      this.root.setAttribute('aria-hidden', 'true');
      this.root.setAttribute('inert', '');
      this.setPageInert(false);
      document.body.classList.remove('is-search-modal-open');
      document.removeEventListener('keydown', this.onKeyDown);
      if (this.input) this.input.value = '';
      this.render('');
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus();
      }
    }

    toggle() {
      if (this.isOpen) this.close();
      else this.open();
    }

    onKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      this.trapFocus(event);
    }
  }

  function scopeContains(scope, node) {
    if (!scope || !node) return false;
    return scope === node || scope.contains(node);
  }

  function init() {
    const root = document.querySelector(ROOT_SEL);
    if (!root) return;
    const controller = new SearchModalController(root);
    window.SearchModal = {
      open: () => controller.open(),
      close: () => controller.close(),
      toggle: () => controller.toggle()
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
